import { API_BASE_URL } from '../lib/constants';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertCircle, CheckCircle2, Shield, Users, Lock } from 'lucide-react';
import axios from 'axios';
import { useNotification } from '../contexts/NotificationContext';

const ResetPassword = () => {
  const { showNotification } = useNotification();
  const { token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [errors, setErrors] = useState({});

  const backgroundImages = [
    {
      url: 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
      title: 'Advanced Medical Technology',
      subtitle: 'State-of-the-art equipment for precise diagnosis',
    },
    {
      url: 'https://images.unsplash.com/photo-1584982751601-97dcc096659c?ixlib=rb-4.0.3&auto=format&fit=crop&w=2072&q=80',
      title: 'Expert Medical Team',
      subtitle: 'Experienced professionals dedicated to your health',
    },
    {
      url: 'https://images.unsplash.com/photo-1579684385127-1ef15d508118?ixlib=rb-4.0.3&auto=format&fit=crop&w=2080&q=80',
      title: 'Comprehensive Patient Care',
      subtitle: 'Personalized treatment plans for every patient',
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === backgroundImages.length - 1 ? 0 : prevIndex + 1
      );
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const validatePassword = (password) => {
    const errors = [];
    if (password.length < 8) errors.push('Password must be at least 8 characters long');
    if (!/[A-Z]/.test(password)) errors.push('Password must contain at least one uppercase letter');
    if (!/[a-z]/.test(password)) errors.push('Password must contain at least one lowercase letter');
    if (!/[0-9]/.test(password)) errors.push('Password must contain at least one number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) errors.push('Password must contain at least one special character');
    return errors;
  };

  const validate = () => {
    const tempErrors = {};
    if (!password.trim()) {
      tempErrors.password = 'Password is required';
    } else {
      const passwordErrors = validatePassword(password);
      if (passwordErrors.length > 0) tempErrors.password = passwordErrors[0];
    }
    if (!confirmPassword.trim()) {
      tempErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      tempErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(tempErrors);
    if (Object.keys(tempErrors).length > 0) {
      showNotification('error', Object.values(tempErrors)[0]);
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE_URL}/reset_password/${token}`, { password });
      showNotification('success', res.data.message || 'Password reset successfully.');
      setTimeout(() => navigate('/'), 2000);
    } catch (err) {
      console.error('Reset password error:', err);
      const msg = err.response?.data?.message || 'Failed to reset password. Link may be expired.';
      showNotification('error', msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative">
      {/* Full Screen Background with Slideshow */}
      <div className="absolute inset-0 z-0">
        {backgroundImages.map((image, index) => (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1500 ease-in-out ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
              }`}
          >
            <div
              className="w-full h-full bg-cover bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${image.url})` }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/40 to-black/60" />
          </div>
        ))}
      </div>

      {/* Content Overlay */}
      <div className="relative z-10 min-h-screen">
        {/* Desktop Layout */}
        <div className="hidden lg:grid lg:grid-cols-12 min-h-screen">
          {/* Left Content - Hero Section */}
          <div className="col-span-7 flex flex-col justify-between p-12 xl:p-16">
            <div className="mb-8">
              <div className="flex items-center space-x-4 mb-8">
                <div className="h-20 w-20 rounded-3xl bg-gradient-to-br from-teal-50 to-cyan-600 flex items-center justify-center shadow-2xl">
                  <img src="/logo.png" alt="Logo" className="h-12 w-12" />
                </div>
                <div className="text-white">
                  <h1 className="text-4xl font-black tracking-tight">Surgix</h1>
                  <p className="text-teal-200 text-lg font-medium">Healthcare Excellence Portal</p>
                </div>
              </div>
            </div>

            <div className="flex-1 flex flex-col justify-center max-w-3xl">
              <div className="space-y-8">
                <h2 className="text-6xl xl:text-7xl font-black text-white leading-tight tracking-tight">
                  {backgroundImages[currentImageIndex].title}
                </h2>
                <p className="text-2xl xl:text-3xl text-teal-100 font-light leading-relaxed">
                  {backgroundImages[currentImageIndex].subtitle}
                </p>
              </div>

              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-16">
                <div className="group bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="flex items-center space-x-4">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-teal-400 to-teal-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Shield className="h-7 w-7 text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="text-xl font-bold">HIPAA Compliant</h3>
                      <p className="text-white/80 text-sm">Enterprise security standards</p>
                    </div>
                  </div>
                </div>
                <div className="group bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20 hover:bg-white/15 transition-all duration-300">
                  <div className="flex items-center space-x-4">
                    <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                      <Users className="h-7 w-7 text-white" />
                    </div>
                    <div className="text-white">
                      <h3 className="text-xl font-bold">24/7 Support</h3>
                      <p className="text-white/80 text-sm">Always here when you need us</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between mt-12">
              <div className="flex space-x-4">
                {backgroundImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`h-3 rounded-full transition-all duration-500 ${index === currentImageIndex
                      ? 'w-16 bg-white shadow-lg'
                      : 'w-3 bg-white/40 hover:bg-white/70'
                      }`}
                  />
                ))}
              </div>
              <div className="text-white/60 text-sm">
                Slide {currentImageIndex + 1} of {backgroundImages.length}
              </div>
            </div>
          </div>

          {/* Right Content - Login Form */}
          <div className="col-span-5 flex items-center justify-center p-8">
            <div className="w-full max-w-lg">
              <div className="bg-white/95 backdrop-blur-2xl rounded-[2rem] shadow-2xl border border-white/30 p-10">
                <div className="text-center mb-10">
                  <h2 className="text-3xl font-black text-slate-800 mb-3">Reset Password</h2>
                  <p className="text-slate-600 text-lg">Enter and confirm your new password below.</p>
                </div>

                {alert.show && (
                  <div
                    className={`mb-8 p-5 rounded-2xl ${alert.type === 'error'
                      ? 'bg-red-50 border-2 border-red-200'
                      : 'bg-green-50 border-2 border-green-200'
                      }`}
                  >
                    <div className="flex items-center space-x-3">
                      {alert.type === 'success' ? (
                        <CheckCircle2 className="h-6 w-6 text-green-600 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="h-6 w-6 text-red-600 flex-shrink-0" />
                      )}
                      <div>
                        <h4
                          className={`font-bold text-lg ${alert.type === 'error' ? 'text-red-800' : 'text-green-800'
                            }`}
                        >
                          {alert.title}
                        </h4>
                        <p
                          className={`text-sm ${alert.type === 'error' ? 'text-red-700' : 'text-green-700'
                            }`}
                        >
                          {alert.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-8">
                  <div className="space-y-3">
                    <label className="flex items-center space-x-2 text-slate-700 font-bold text-sm">
                      <Lock className="h-4 w-4" />
                      <span>New Password</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className={`w-full h-16 px-6 pr-16 text-lg bg-slate-50 border-2 rounded-2xl focus:ring-4 focus:ring-teal-100 focus:outline-none transition-all duration-300 text-slate-800 placeholder-slate-400 hover:border-slate-300 ${errors.password ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-teal-500'}`}
                        disabled={loading}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-teal-600 transition-colors p-2"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff className="h-6 w-6" /> : <Eye className="h-6 w-6" />}
                      </button>
                    </div>
                    {errors.password && (
                      <p className="text-red-500 text-sm mt-1">{errors.password}</p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <label className="flex items-center space-x-2 text-slate-700 font-bold text-sm">
                      <Lock className="h-4 w-4" />
                      <span>Confirm Password</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Re-enter your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className={`w-full h-16 px-6 pr-16 text-lg bg-slate-50 border-2 rounded-2xl focus:ring-4 focus:ring-teal-100 focus:outline-none transition-all duration-300 text-slate-800 placeholder-slate-400 hover:border-slate-300 ${errors.confirmPassword ? 'border-red-500 focus:border-red-500' : 'border-slate-200 focus:border-teal-500'}`}
                        disabled={loading}
                        required
                        onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-teal-600 transition-colors p-2"
                        disabled={loading}
                      >
                        {showConfirmPassword ? <EyeOff className="h-6 w-6" /> : <Eye className="h-6 w-6" />}
                      </button>
                    </div>
                    {errors.confirmPassword && (
                      <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full h-16 bg-gradient-to-r from-teal-600 via-teal-500 to-cyan-500 hover:from-teal-700 hover:via-teal-600 hover:to-cyan-600 text-white font-bold text-xl rounded-2xl shadow-2xl hover:shadow-teal-500/25 transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed disabled:transform-none"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-3">
                        <div className="w-6 h-6 border-3 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Resetting Password...</span>
                      </div>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>

                <div className="mt-10 pt-8 border-t border-slate-200 text-center">
                  <p className="text-sm text-slate-500">Secure Healthcare Portal &copy; 2025 Surgix</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile Layout */}
        <div className="lg:hidden min-h-screen flex flex-col">
          <div className="relative z-20 p-6 pt-12">
            <div className="flex items-center justify-center space-x-4 mb-8">
              <div className="h-16 w-16 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-600 flex items-center justify-center shadow-xl">
                <img src="/logo.png" alt="Logo" className="h-12 w-12" />
              </div>
              <div className="text-white text-center">
                <h1 className="text-3xl font-black">Surgix</h1>
                <p className="text-teal-200 font-medium">Healthcare Portal</p>
              </div>
            </div>
          </div>

          <div className="relative z-20 text-center px-6 mb-8">
            <h2 className="text-4xl sm:text-5xl font-black text-white leading-tight mb-4">
              {backgroundImages[currentImageIndex].title}
            </h2>
            <p className="text-lg sm:text-xl text-white/90 font-light max-w-md mx-auto">
              {backgroundImages[currentImageIndex].subtitle}
            </p>
          </div>

          <div className="flex-1 flex items-start justify-center px-4 pb-8 relative z-20">
            <div className="w-full max-w-sm">
              <div className="bg-white/95 backdrop-blur-2xl rounded-[2rem] shadow-2xl border border-white/30 p-8">
                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold text-slate-800 mb-2">Reset Password</h3>
                  <p className="text-slate-600">Enter and confirm your new password below.</p>
                </div>

                {alert.show && (
                  <div
                    className={`mb-6 p-4 rounded-xl ${alert.type === 'error'
                      ? 'bg-red-50 border-l-4 border-red-500'
                      : 'bg-green-50 border-l-4 border-green-500'
                      }`}
                  >
                    <div className="flex items-start space-x-2">
                      {alert.type === 'success' ? (
                        <CheckCircle2 className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                      )}
                      <div>
                        <h4
                          className={`font-bold text-sm ${alert.type === 'error' ? 'text-red-800' : 'text-green-800'
                            }`}
                        >
                          {alert.title}
                        </h4>
                        <p
                          className={`text-xs ${alert.type === 'error' ? 'text-red-700' : 'text-green-700'
                            }`}
                        >
                          {alert.message}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-slate-700 font-bold text-sm flex items-center space-x-2">
                      <Lock className="h-4 w-4" />
                      <span>New Password</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your new password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full h-14 px-4 pr-12 text-base bg-slate-50 border-2 border-slate-200 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 focus:outline-none transition-all duration-300 text-slate-800 placeholder-slate-400"
                        disabled={loading}
                        required
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-teal-600 transition-colors p-1"
                        disabled={loading}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-slate-700 font-bold text-sm flex items-center space-x-2">
                      <Lock className="h-4 w-4" />
                      <span>Confirm Password</span>
                    </label>
                    <div className="relative">
                      <input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder="Re-enter your password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full h-14 px-4 pr-12 text-base bg-slate-50 border-2 border-slate-200 rounded-xl focus:border-teal-500 focus:ring-4 focus:ring-teal-100 focus:outline-none transition-all duration-300 text-slate-800 placeholder-slate-400"
                        disabled={loading}
                        required
                        onKeyDown={(e) => e.key === 'Enter' && handleSubmit(e)}
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-teal-600 transition-colors p-1"
                        disabled={loading}
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <button
                    type="submit"
                    className="w-full h-14 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700 text-white font-bold text-lg rounded-xl shadow-xl hover:shadow-2xl transform hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={loading}
                  >
                    {loading ? (
                      <div className="flex items-center justify-center space-x-2">
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                        <span>Resetting Password...</span>
                      </div>
                    ) : (
                      'Reset Password'
                    )}
                  </button>
                </form>

                <div className="mt-8 pt-6 border-t border-slate-200 text-center">
                  <p className="text-xs text-slate-500">&copy; 2025 Surgix • Secure Portal</p>
                </div>
              </div>
            </div>
          </div>

          <div className="relative z-20 flex justify-center pb-8">
            <div className="flex space-x-3">
              {backgroundImages.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentImageIndex(index)}
                  className={`h-2 rounded-full transition-all duration-300 ${index === currentImageIndex
                    ? 'w-8 bg-white'
                    : 'w-2 bg-white/50 hover:bg-white/80'
                    }`}
                />
              ))}
            </div>
          </div>
        </div>

        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute top-20 left-20 w-4 h-4 bg-teal-400/30 rounded-full animate-pulse"></div>
          <div className="absolute top-1/3 right-20 w-2 h-2 bg-cyan-300/40 rounded-full animate-bounce"></div>
          <div className="absolute bottom-1/4 left-1/4 w-3 h-3 bg-white/20 rounded-full animate-ping"></div>
          <div className="absolute bottom-1/3 right-1/3 w-1 h-1 bg-teal-300/50 rounded-full animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;